/// <reference path="../../css.d.ts" />
"use client";
// @ubm/ui/components/select — LE Select canonique (lot U1, 05/10/2026). UN moteur : l'implémentation Radix existante de
// `primitives/Select` (historique ci-dessous, contrats intacts) DÉPLACÉE ici, enrichie de l'API et du mouvement UIArc
// (`registry/components/select/select.tsx` @ 7238f98196b147a8d50ce467abcbad310d4d8414, MIT) :
//   · `label` / `description` (enveloppe `.field`, label relié au déclencheur, aide reliée par `aria-describedby`) ;
//   · peinture et mouvement UIArc POUR TOUS (arbitrage Root 05/10 : canon par défaut, aucun opt-in) : valeur visible qui
//     ROULE dans le sens de la liste (texte réel conservé pour les technologies d'assistance dans un `Value` masqué), chevron
//     qui pivote, panneau animé depuis le bord du déclencheur, indicateur coché animé, flèches de défilement, variantes
//     mouvement réduit — repris de la source. Les classes historiques (crochets `ubm-ui-*`, utilitaires E136, peau native)
//     restent posées pour la compatibilité ; le module UIArc, hors couche, peint par-dessus les utilitaires en couche.
//     `presentation` native/apple reste acceptée (crochets/attributs) ; la feuille Apple lit désormais les jetons --arc-* ;
//   · `Select` (entrée `@ubm/ui/components/select`) et `SelectBase` (façade `@ubm/ui` Select) = le même composant.
//   · engagement d'hôte U2 (`useOverlayHostEngaged` de @ubm/ui/overlays) : hôte désengagé → ni Portal, ni Content, ni
//     FocusScope / verrou / masquage Radix ; l'état ouvert et la valeur restent mémorisés, aucun `onOpenChange` induit.
// Ce que la source n'avait pas et que le moteur garde pour TOUTES les présentations : pont <select> natif (nom, requis, form,
// defaultValue, reset, FormData, autoComplete, fieldset), option `""` réelle, groupes, `input` émis pour un choix ADOPTÉ
// seulement, Field (id, aria-describedby, aria-invalid, ref), marque de couche non modale de @ubm/ui/overlays.
// Dépendance U2 : `useOverlayHostEngaged` est le contrat nommé reçu (OverlayHost.tsx, arbre host) ; sa source n'est pas encore
// dans l'arbre D — composition Root requise (U1/resume-causal-20261004T2254Z/FIN.md).
//
// —— Historique du moteur (primitives/Select.tsx avant U1) ——
// Phase 2 (form P2) — Select : liste déroulante, Radix Select wrappé (FR10 : @radix-ui/react-select
// importé UNIQUEMENT ici → swappable, jamais leaké). API ergonomique `options` (controlled : value +
// onValueChange ; OU non-contrôlé : defaultValue). Le déclencheur (role combobox) et le panneau sont
// tokens-only (FR1) : bg-surface/border-line au repos, surface-raised + élévation pour le menu, l'option
// active en bg-surface-selected. invalid → border-danger-line. focus-ring dérivé. L'a11y (clavier, aria,
// type-ahead) vient de la lib. brand-AGNOSTIC, zéro marge externe, forwardRef sur le déclencheur.
//
// PRÉSENTATION (03/10/2026) — la peau seulement : même Radix Select (contrôlé / non contrôlé, type-ahead, options
// désactivées, événements, ref sur le déclencheur, `name` de formulaire). Contrat avec `native-fields-apple.css` :
//   · classes STRUCTURELLES toujours posées : déclencheur `ubm-ui-field ubm-ui-select-trigger`, chevrons
//     `ubm-ui-select-chevron` (+ `-native` / `-apple`), panneau `ubm-ui-select-content`, liste `ubm-ui-select-viewport`,
//     option `ubm-ui-select-item`, colonne de coche `ubm-ui-select-check` (masquée en natif) ;
//   · activation par ANCÊTRE `body[data-ui-presentation="apple"]` — le panneau est en portail SOUS <body> : la même
//     activation le couvre, sans prop ni contexte ; hors d'un body marqué, rendu natif ;
//   · `presentation="apple"` (prop) : `data-ui-presentation="apple"` sur le déclencheur ET le panneau, peau Foundry retirée
//     (hors Admin aussi) ; `presentation="native"` : désinscription explicite ; prop absente : suit le body ;
//   · `appleAppearance` (présentation `apple` explicite) : `data-ui-appearance` sur déclencheur et panneau, pour un
//     portail hors de tout body marqué. Sous un body marqué, l'apparence et les jetons viennent du body (root).
//
// OPTION VIDE SÉLECTIONNABLE (04/10/2026, lane E agenda_controls) — `{ value: "", label: "Choisir un droit" }` est une option
// comme les autres : choisie, elle rend `""` au callback, à `value`/`defaultValue` et au FormData. Radix interdit un Item à
// valeur vide (le vide y signifie « placeholder ») : la correspondance `""` ↔ `EMPTY_ITEM` vit UNIQUEMENT entre ce composant
// et Radix — aucun consommateur, callback ni FormData ne la voit. Le formulaire natif (nom, valeur, `required`, `disabled`,
// fieldset désactivé, réinitialisation, remplissage automatique) passe par le <select> natif de CE composant (même rôle que
// celui de Radix, qui ne peut pas porter le vide) : l'option vide y est en TÊTE, donc option « libellé de substitution » du
// HTML — `required` + vide est invalide, comme un <select> natif. Radix ne reçoit plus `name`/`required` : son <select> caché
// n'est ni soumis ni validé.
//
// GROUPES D'OPTIONS (04/10/2026, lot G relations_commercial) — `option.group` (facultatif) : les options consécutives d'un même
// `group` sont rendues dans un Radix `Group` étiqueté par un Radix `Label` (rôle `group`, nommé par son étiquette), dans l'ORDRE
// de `options` ; le <select> natif du formulaire les place dans un <optgroup label> équivalent. Même Root, même portail, mêmes
// touches, mêmes valeurs (callback et FormData voient la valeur réelle de l'option, jamais le nom du groupe). Une option sans
// `group` reste à sa place, hors groupe ; une liste sans aucun `group` rend exactement le même arbre qu'avant.
//
// COUCHE ET DESCRIPTIONS (04/10/2026, lot K) — le panneau ouvert porte la marque de couche NON modale de @ubm/ui/overlays
// (`data-ubm-overlay="non-modal"`) : un overlay parent (Dialog, peek, popup) lui laisse son Échap et son Tab, Radix garde
// clavier, type-ahead, focus, restitution et fermeture. Fermé, Radix ne monte pas le panneau dans le document : la marque
// disparaît avec lui. `aria-describedby` (aide et erreur câblées par <Field>, ou description du consommateur) est posé sur
// le DÉCLENCHEUR visible, comme `aria-label`/`aria-invalid` ; il n'allait auparavant qu'au Root Radix, qui l'ignorait.
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/cn";
import { motionTokens } from "../../lib/motion-tokens";
// Fondation UIArc (jetons --arc-*) embarquée par le composant : aucun import d'app ni fournisseur requis.
import "../../styles/foundation.css";
import { marqueOverlayNonModal } from "../../overlays/useOverlayAccessible";
import { consommerEchap, echapConsomme, useOverlayHostEngaged } from "../../overlays";
import type { FieldPresentation } from "../input/input";
import styles from "./select.module.css";

/** Présentations acceptées (compatibilité des crochets/attributs historiques) ; la peinture est UIArc dans les deux cas. */
export type SelectPresentation = FieldPresentation;

/** The shown value rolls in the direction of the list: a later option rises from below, an earlier one drops from above. */
const valueRoll: Variants = {
  enter: (direction: number) => ({ opacity: 0, y: `${direction * 0.35}em`, filter: `blur(${motionTokens.blur.soft}px)` }),
  center: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] } },
  exit: (direction: number) => ({ opacity: 0, y: `${direction * -0.3}em`, filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } }),
};
/** Reduced motion keeps a short crossfade; the resting state matches valueRoll so server and client markup agree. */
const valueFade: Variants = { enter: { opacity: 0 }, center: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: motionTokens.duration.instant } }, exit: { opacity: 0, transition: { duration: motionTokens.duration.instant } } };

/** Placeholder par défaut de l'API UIArc — seulement quand l'API UIArc est employée (`label` fourni) ; l'API historique n'en
 *  impose aucun (aucun texte anglais injecté dans un écran Foundry qui n'en demande pas). */
const ARC_PLACEHOLDER = "Select an option";

// E136 — LE LIBELLÉ LONG NE DÉBORDE PLUS. Mesuré à 1280 px dans le composeur : une option au libellé long
// (« Le calendrier intégré à la page », « Une citation en avant ») poussait le chevron hors du déclencheur, et
// dans le panneau elle sortait de la ligne — coupée net par `overflow-hidden` du panneau, donc ILLISIBLE, sans
// que rien ne signale qu'il manquait du texte. Trois corrections, toutes portées ici (c'est LE Select du DS) :
//   1. le déclencheur tronque proprement (`min-w-0` + `truncate`) et le chevron ne se comprime jamais ;
//   2. le panneau a une largeur MAXIMALE, bornée par la fenêtre — il ne sort plus de l'écran ;
//   3. une option qui ne tient pas sur une ligne PASSE À LA LIGNE au lieu d'être rognée (hauteur minimale,
//      plus hauteur fixe) : on préfère une option haute à une option amputée.
// Ces trois corrections valent pour les DEUX présentations (seule la peau change).
const triggerVariants = cva(
  "ubm-ui-field ubm-ui-select-trigger inline-flex w-full min-w-0 items-center justify-between gap-inline-sm " +
    "transition-colors disabled:pointer-events-none",
  {
    variants: {
      size: {
        sm: "h-control-sm px-inline-sm text-micro",
        md: "h-control-md px-inline-md text-body",
        lg: "h-control-lg px-inline-lg text-body-lg",
      },
      presentation: {
        native:
          "font-sans bg-surface text-fg border border-line rounded-md data-[placeholder]:text-fg-faint " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring " +
          "disabled:opacity-60 aria-[invalid=true]:border-danger-line",
        apple: "",
      },
    },
    defaultVariants: { size: "md", presentation: "native" },
  },
);

/** Mise en page du panneau, commune aux deux présentations (E136 : largeur bornée, rien de rogné en largeur). */
const CONTENT_LAYOUT =
  "ubm-ui-select-content z-popover min-w-[var(--radix-select-trigger-width)] max-w-[min(28rem,calc(100vw-2rem))] overflow-hidden";
const CONTENT_SKIN: Record<SelectPresentation, string> = {
  native: "bg-surface-raised border border-line rounded-md shadow-overlay",
  apple: "",
};
/** Mise en page d'une option, commune (E136 : `min-h` et non `h` — un libellé long se replie au lieu d'être rogné). */
const ITEM_LAYOUT =
  "ubm-ui-select-item relative flex cursor-default select-none items-center gap-inline-sm " +
  "min-h-control-sm py-1 px-inline-sm outline-none whitespace-normal break-words " +
  "data-[disabled]:pointer-events-none";
const ITEM_SKIN: Record<SelectPresentation, string> = {
  native:
    "font-sans text-body text-fg rounded-sm data-[highlighted]:bg-surface-hover data-[state=checked]:bg-surface-selected " +
    "data-[disabled]:opacity-60",
  apple: "",
};

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  /** Étiquette du groupe de l'option (ex. « Personnes ») : les options consécutives d'un même groupe sont regroupées sous elle. */
  group?: string;
}

/** Étiquette d'un groupe dans le panneau : classe structurelle + tokens (aucune peau locale). */
const GROUP_LABEL = "ubm-ui-select-group-label px-inline-sm pt-1 pb-0.5 text-micro font-medium text-fg-muted select-none";

/** Découpe en suites consécutives de même `group` (ordre conservé) ; une option sans groupe forme une suite sans étiquette. */
function segmentsOf(options: readonly SelectOption[]): Array<{ group: string | undefined; options: SelectOption[] }> {
  const out: Array<{ group: string | undefined; options: SelectOption[] }> = [];
  for (const o of options) {
    const last = out[out.length - 1];
    if (last && last.group === o.group) last.options.push(o);
    else out.push({ group: o.group, options: [o] });
  }
  return out;
}

/** Valeur interne transmise à Radix pour l'option `""` (jamais exposée : callback, props et FormData voient `""`). */
const EMPTY_ITEM = "\u0000ubm-select-empty";
const toRadix = (v: string) => (v === "" ? EMPTY_ITEM : v);
const fromRadix = (v: string) => (v === EMPTY_ITEM ? "" : v);

/** Masquage du <select> natif de formulaire : mêmes styles que le <select> caché de Radix (lisible des formulaires seulement). */
const NATIVE_BRIDGE_STYLE: React.CSSProperties = {
  position: "absolute",
  border: 0,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  wordWrap: "normal",
};

/** Apparence transmise au panneau en portail (présentation `apple` explicite) : il n'hérite d'aucun scope de fenêtre. */
export type SelectAppleAppearance = "light" | "dark" | "system";

export interface SelectProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>, "children">,
    Omit<VariantProps<typeof triggerVariants>, "presentation"> {
  /** Options (lecture seule acceptée : façades et appelants passent souvent des constantes `as const`). */
  options: readonly SelectOption[];
  /** Texte de substitution (nœud React accepté, ex. libellé composé de la façade product). */
  placeholder?: React.ReactNode;
  /** État d'erreur : pose la bordure danger + aria-invalid (l'enveloppe Field le branche aussi). */
  invalid?: boolean;
  /** Attributs transmis au déclencheur (aria-label, id, className…). */
  className?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  /** Identifiants des descriptions (aide, erreur) — posés sur le déclencheur ; <Field> les câble. */
  "aria-describedby"?: string;
  id?: string;
  /**
   * Peau du déclencheur ET du panneau. Absente : suit `body[data-ui-presentation]` (natif hors body marqué). `apple` :
   * active ici, même hors Admin. `native` : reste natif même sous un body marqué `apple`. Peinture et mouvement UIArc dans
   * tous les cas (crochets et attributs seuls changent).
   */
  presentation?: SelectPresentation;
  /** Libellé visible (API UIArc) : enveloppe `.field`, `<label htmlFor>` relié au déclencheur. Absent : aucun DOM ajouté. */
  label?: React.ReactNode;
  /** Aide visible (API UIArc) sous le déclencheur, reliée par `aria-describedby` après les descriptions reçues. */
  description?: React.ReactNode;
  /**
   * Présentation `apple` explicite : apparence claire / sombre / système, posée en `data-ui-appearance` sur le déclencheur
   * et sur le panneau en portail. Ignorée sinon (sous un body marqué, l'apparence est celle du body).
   */
  appleAppearance?: SelectAppleAppearance;
  /** Info-bulle native du déclencheur (vrai <button> combobox). */
  title?: string;
  /** Étiquetage par référence (ex. libellé externe), posé sur le déclencheur. */
  "aria-labelledby"?: string;
  /**
   * Lot A3 (05/10, additif) — rechoisir l'option DÉJÀ sélectionnée la DÉSÉLECTIONNE : `onValueChange("")` UNE fois (clic, Entrée ou
   * Espace sur cette option), puis le panneau se ferme par le moteur Radix existant. Défaut `false` : rechoisir ne fait rien (comportement
   * d'avant, aucun rappel). Une option désactivée, ou l'option vide `""`, ne déclenche jamais la désélection.
   */
  deselectable?: boolean;
  /** Attributs `data-*` (ex. `data-field` des formulaires) transmis au déclencheur. */
  [dataAttribute: `data-${string}`]: string | number | boolean | undefined;
}

/** Props de `SelectPrimitive.Root` : tout le reste va au VRAI déclencheur (title, aria-*, data-*…). */
const ROOT_KEYS = new Set(["open", "defaultOpen", "onOpenChange", "dir"]);

export const SelectBase = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectProps
>(
  (
    {
      options,
      placeholder,
      size,
      invalid,
      className,
      id,
      "aria-label": ariaLabel,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      presentation,
      appleAppearance,
      label,
      description,
      value,
      defaultValue,
      onValueChange,
      name,
      required,
      form,
      autoComplete,
      disabled,
      open: openProp,
      defaultOpen,
      onOpenChange,
      deselectable = false,
      ...rest
    },
    ref,
  ) => {
    const skin: SelectPresentation = presentation === "apple" ? "apple" : "native";
    const reduceMotion = useReducedMotion();
    const engaged = useOverlayHostEngaged();
    // Répartition : les props propres au Root Radix d'un côté ; tout attribut DOM restant (title, aria-labelledby, data-field,
    // data-*…) va au VRAI déclencheur — Radix Root les ignorait silencieusement.
    const rootProps: Record<string, unknown> = {};
    const triggerAttrs: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) (ROOT_KEYS.has(k) ? rootProps : triggerAttrs)[k] = v;
    // Ouverture : contrôlée par le parent OU mémorisée ici (un hôte désengagé démonte le panneau sans perdre l'état ouvert).
    const [openInner, setOpenInner] = React.useState(defaultOpen ?? false);
    const isOpenControlled = openProp !== undefined;
    const currentOpen = isOpenControlled ? openProp : openInner;
    // Lot A3 (correction) — intention de rechoix posée par le geste sur l'option choisie (voir `deselectHandlers`) : consommée SEULEMENT
    // si Radix valide réellement la sélection dans ce même geste (son `handleSelect` ferme par `onOpenChange(false)`).
    const rechoiceRef = React.useRef<object | null>(null);
    const setOpen = (next: boolean) => {
      if (!next && rechoiceRef.current !== null) { rechoiceRef.current = null; handleChoice(""); }
      if (!isOpenControlled) setOpenInner(next);
      onOpenChange?.(next);
    };
    // Échap — arbitre unique U2 (« cédé ≠ consommé ») : déjà CONSOMMÉ par une couche au-dessus → Radix ne ferme pas (il lit
    // `defaultPrevented`) ; CÉDÉ par une couche Radix qui n'était pas la plus haute → libre : ce panneau le prend, le consomme
    // et se ferme ; sinon Radix ferme et son `preventDefault` vaut consommation pour les couches suivantes.
    const onEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented && !echapConsomme(event)) {
        consommerEchap(event);
        setOpen(false);
      }
    };
    // API UIArc : libellé et aide reliés au déclencheur (id généré si le consommateur n'en fournit pas).
    const generatedId = React.useId();
    const controlId = id ?? (label != null ? generatedId : undefined);
    const hintId = description != null ? `${controlId ?? generatedId}-description` : undefined;
    const describedBy = [ariaDescribedBy, hintId].filter(Boolean).join(" ") || undefined;
    const marks = {
      "data-ui-presentation": presentation,
      "data-ui-appearance": presentation === "apple" ? appleAppearance : undefined,
    };
    // Valeur courante, contrôlée OU non : la même sémantique qu'avant, `""` compris (option vide choisie).
    const isControlled = value !== undefined;
    const [inner, setInner] = React.useState<string | undefined>(defaultValue);
    const hasEmptyOption = options.some((o) => o.value === "");
    // Sans valeur et avec une option vide : c'est elle qui est choisie (comme l'option de tête d'un <select> natif).
    const current = (isControlled ? value : inner) ?? (hasEmptyOption ? "" : undefined);
    // Radix : `""` = placeholder ; l'option vide passe par sa valeur interne.
    const radixValue = current === undefined ? "" : hasEmptyOption ? toRadix(current) : current;
    // Valeur visible UIArc et sens de roulement (index croissant → monte du bas ; décroissant → descend du haut).
    const shownIndex = current === undefined ? -1 : options.findIndex((o) => o.value === current);
    const shownOption = shownIndex >= 0 ? options[shownIndex] : undefined;
    const [previousIndex, setPreviousIndex] = React.useState(shownIndex);
    const [direction, setDirection] = React.useState(1);
    if (previousIndex !== shownIndex) { setPreviousIndex(shownIndex); setDirection(shownIndex > previousIndex ? 1 : -1); }
    const handleValueChange = (next: string) => {
      const real = fromRadix(next);
      if (!isControlled) setInner(real);
      onValueChange?.(real);
    };

    // Déclencheur visible : le `ref` du consommateur ET le nôtre (le refus de validation native y ramène le focus).
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const setTriggerRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // <select> natif du formulaire (nom, valeur réelle, requis, désactivé, réinitialisation, remplissage automatique).
    const bridgeRef = React.useRef<HTMLSelectElement>(null);
    const bridgeValue = current ?? "";
    const withBridge = name !== undefined || required === true;
    const resetTo = isControlled ? bridgeValue : (defaultValue ?? "");
    // Choix fait dans la liste visible (Radix) : un <select> natif émet `input` quand l'utilisateur choisit — les gardes de saisie
    // des formulaires (ex. `onInput` du formulaire hôte) l'écoutent. Le <select> du formulaire émet donc `input` (bouillonnant),
    // UNE fois, après avoir pris la nouvelle valeur. Pas d'émission pour un changement venu du <select> natif lui-même (le
    // navigateur l'a déjà émis), ni pour une réinitialisation, ni pour une valeur que le parent contrôlé n'a pas adoptée.
    // Chaque choix est examiné dans SA propre passe de rendu : le compteur local `choiceTick` force cette passe même quand le parent
    // contrôlé refuse (valeur inchangée). Le choix y est vidé, adopté ou non : une relecture ultérieure du parent avec la valeur
    // autrefois cliquée ne trouve plus rien à émettre (aucune saisie fantôme). Adopté = valeur du pont déjà à jour dans cette passe.
    const pendingInput = React.useRef<string | null>(null);
    const [choiceTick, setChoiceTick] = React.useState(0);
    const handleChoice = (next: string) => {
      pendingInput.current = fromRadix(next);
      setChoiceTick((t) => t + 1);
      handleValueChange(next);
    };
    React.useEffect(() => {
      const chosen = pendingInput.current;
      if (chosen === null) return;
      pendingInput.current = null;
      if (chosen === bridgeValue) bridgeRef.current?.dispatchEvent(new Event("input", { bubbles: true }));
    }, [choiceTick, bridgeValue]);
    React.useEffect(() => {
      const owner = bridgeRef.current?.form;
      if (!withBridge || !owner) return;
      const onReset = () => {
        // L'événement `reset` PRÉCÈDE la remise à zéro du navigateur, qui ramènerait le <select> contrôlé sur sa première
        // option : la valeur réelle est rétablie à la tâche SUIVANTE (après la remise à zéro, que le reset vienne d'un clic ou
        // de `form.reset()`) — contrôlé : la valeur du parent ; non contrôlé : `defaultValue`, état React compris. Aucun rappel :
        // une réinitialisation n'est pas un choix de l'utilisateur.
        if (!isControlled) setInner(defaultValue);
        window.setTimeout(() => {
          if (bridgeRef.current) bridgeRef.current.value = resetTo;
        }, 0);
      };
      owner.addEventListener("reset", onReset);
      return () => owner.removeEventListener("reset", onReset);
    }, [withBridge, isControlled, defaultValue, resetTo]);

    // Lot A3 — désélection par rechoix. Radix ne rappelle pas `onValueChange` pour la valeur déjà choisie (il ferme seulement) : les
    // gestionnaires de l'OPTION choisie (composés AVANT ceux de Radix, qui gardent la fermeture) font émettre `""` — à la fermeture
    // validée par Radix — par le chemin de choix existant (`handleChoice` : pont natif, événement `input`, contrôlé / non contrôlé). Mêmes gestes que Radix : relâchement souris,
    // clic tactile/stylet, Entrée ou Espace. Aucun autre état, aucune autre option touchée.
    // Le type de pointeur est suivi sur TOUTES les options (comme Radix) : le clic qui suit le relâchement souris d'un choix rend
    // l'option fraîchement choisie « sélectionnée » — sans ce suivi, ce clic la désélectionnerait aussitôt.
    // Correction : les gestionnaires ne VIDENT plus eux-mêmes ; ils posent l'intention, et la décision reste celle de Radix — Espace
    // pendant une recherche au clavier (typeahead) n'est pas une sélection pour Radix, donc pas un rechoix. Jeton levé en microtâche :
    // Radix décide synchrone dans le même événement ; sans sélection, l'intention ne survit pas au geste (ni Échap, ni voile ensuite).
    const intendRechoice = () => {
      const token = {};
      rechoiceRef.current = token;
      queueMicrotask(() => { if (rechoiceRef.current === token) rechoiceRef.current = null; });
    };
    const pointerTypeRef = React.useRef<string>("touch");
    const trackPointer = { onPointerDown: (e: React.PointerEvent) => { pointerTypeRef.current = e.pointerType; } };
    const deselectsOn = (opt: SelectOption) => deselectable && !disabled && !opt.disabled && opt.value !== "" && opt.value === current;
    const deselectHandlers = (opt: SelectOption) =>
      !deselectable
        ? {}
        : !deselectsOn(opt)
          ? trackPointer
          : {
            ...trackPointer,
            // `defaultPrevented` : Radix annule lui-même le relâchement qui suit l'ouverture (pointeur resté près du déclencheur) — même refus.
            onPointerUp: (e: React.PointerEvent) => { if (!e.defaultPrevented && e.pointerType === "mouse") intendRechoice(); },
            onClick: (e: React.MouseEvent) => { if (!e.defaultPrevented && pointerTypeRef.current !== "mouse") intendRechoice(); },
            onKeyDown: (e: React.KeyboardEvent) => { if (!e.defaultPrevented && (e.key === "Enter" || e.key === " ")) intendRechoice(); },
          };

    // Groupes : seulement si au moins une option en déclare un (sinon, arbre inchangé).
    const grouped = options.some((o) => o.group !== undefined);
    const renderItem = (opt: SelectOption) => (
      <SelectPrimitive.Item
        key={opt.value}
        value={toRadix(opt.value)}
        disabled={opt.disabled}
        className={cn(ITEM_LAYOUT, ITEM_SKIN[skin], styles.item)}
        {...deselectHandlers(opt)}
      >
        <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
        {/* UNE coche : l'indicateur UIArc animé (à droite), logé dans la colonne historique `.ubm-ui-select-check` (crochet
            conservé ; `hidden` utilitaire en couche, le module UIArc hors couche l'affiche). Radix ne la rend que pour
            l'option choisie. */}
        <span className={cn("ubm-ui-select-check hidden", styles.indicatorSlot)} aria-hidden="true">
          <SelectPrimitive.ItemIndicator className={styles.indicator}>
            <Check size={16} strokeWidth={1.75} aria-hidden="true" />
          </SelectPrimitive.ItemIndicator>
        </span>
      </SelectPrimitive.Item>
    );
    const renderNativeOption = (o: SelectOption) => (
      <option key={o.value} value={o.value} disabled={o.disabled}>
        {typeof o.label === "string" ? o.label : o.value}
      </option>
    );

    const effectivePlaceholder = placeholder ?? (label != null ? ARC_PLACEHOLDER : undefined);
    const shown = shownOption ? shownOption.label : effectivePlaceholder;
    const root = (
      <SelectPrimitive.Root {...rootProps} open={currentOpen} onOpenChange={setOpen} disabled={disabled} value={radixValue} onValueChange={handleChoice}>
        <SelectPrimitive.Trigger
          ref={setTriggerRef}
          {...triggerAttrs}
          id={controlId}
          data-ubm-canon=""
          aria-label={ariaLabel}
          // `required` va au <select> natif (validation réelle) : l'exigence est AUSSI annoncée sur le combobox visible.
          aria-required={required ? true : undefined}
          aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
          aria-describedby={describedBy}
          className={cn(triggerVariants({ size, presentation: skin }), styles.trigger, className)}
          {...marks}
        >
          {/* Radix ne transmet PAS de className à `Value` (mesuré : il rend un <span> nu) — la troncature vit sur une enveloppe
              à nous (E136). Dedans, la source UIArc : Radix garde la valeur réelle pour les technologies d'assistance ; la copie
              visible roule entre les valeurs. */}
          <span className={cn("min-w-0 flex-1 truncate text-left", styles.valueText)}>
            <span className={styles.srOnly}><SelectPrimitive.Value placeholder={effectivePlaceholder} /></span>
            <span className={styles.valueRoll} aria-hidden="true">
              <AnimatePresence initial={false} custom={direction}>
                <motion.span key={shownOption ? `value-${shownOption.value}` : "placeholder"} data-placeholder={shownOption ? undefined : ""} custom={direction} variants={reduceMotion ? valueFade : valueRoll} initial="enter" animate="center" exit="exit">{shown}</motion.span>
              </AnimatePresence>
            </span>
          </span>
          {/* Deux glyphes toujours rendus : chevron simple (natif, visible par défaut) et double chevron du menu local
              macOS (masqué par défaut) ; la feuille Apple les permute quand la présentation est active. */}
          {/* Chevron UIArc (pivote à l'ouverture), sous les crochets historiques ; le double chevron macOS reste masqué. */}
          <SelectPrimitive.Icon className={cn("ubm-ui-select-chevron shrink-0", skin === "native" && "text-fg-muted", styles.chevron)}>
            <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" className="ubm-ui-select-chevron-native size-4" />
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="ubm-ui-select-chevron-apple hidden size-4">
              <path d="M5 6.5L8 3.5l3 3M5 9.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        {/* Hôte désengagé (U2) : rien de monté — ni portail, ni FocusScope, ni verrou, ni masquage ; Radix garde l'état. */}
        {engaged ? (
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            collisionPadding={12}
            onEscapeKeyDown={onEscapeKeyDown}
            className={cn(CONTENT_LAYOUT, CONTENT_SKIN[skin], styles.content)}
            data-ubm-canon=""
            {...marks}
            {...marqueOverlayNonModal}
          >
            <SelectPrimitive.ScrollUpButton className={styles.scrollButton}>
              <ChevronUp size={16} strokeWidth={1.75} aria-hidden="true" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className={cn("ubm-ui-select-viewport", skin === "native" && "p-inset-xs", styles.viewport)}>
              {grouped
                ? segmentsOf(options).map((seg, i) =>
                    seg.group === undefined ? (
                      <React.Fragment key={`s${i}`}>{seg.options.map(renderItem)}</React.Fragment>
                    ) : (
                      <SelectPrimitive.Group key={`g${i}:${seg.group}`} className="ubm-ui-select-group">
                        <SelectPrimitive.Label className={cn(GROUP_LABEL, styles.groupLabel)}>{seg.group}</SelectPrimitive.Label>
                        {seg.options.map(renderItem)}
                      </SelectPrimitive.Group>
                    ),
                  )
                : options.map(renderItem)}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className={styles.scrollButton}>
              <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
        ) : null}
        {withBridge ? (
          // APRÈS le déclencheur : dans un <label> enveloppant, le premier élément étiquetable reste le déclencheur (son nom).
          <select
            ref={bridgeRef}
            aria-hidden
            tabIndex={-1}
            name={name}
            required={required}
            disabled={disabled}
            form={form}
            autoComplete={autoComplete}
            value={bridgeValue}
            onChange={(e) => handleValueChange(e.target.value)}
            // Refus de validation native (`required` vide) : le navigateur focalise le contrôle invalide, ici masqué. Le focus
            // est rendu au déclencheur VISIBLE ; la validation elle-même (invalidité, message, soumission bloquée) est intacte.
            onFocus={() => triggerRef.current?.focus()}
            style={NATIVE_BRIDGE_STYLE}
          >
            {/* Vide en TÊTE (option « libellé de substitution » du HTML : `required` + vide = invalide), jamais désactivé : une
                option choisie et désactivée n'est pas soumise (le nom disparaîtrait du FormData). Présent aussi sans option vide
                déclarée, pour l'absence de choix — la liste visible ne la propose alors pas. */}
            <option value="" />
            {grouped
              ? segmentsOf(options.filter((o) => o.value !== "")).map((seg, i) =>
                  seg.group === undefined ? (
                    <React.Fragment key={`s${i}`}>{seg.options.map(renderNativeOption)}</React.Fragment>
                  ) : (
                    <optgroup key={`g${i}:${seg.group}`} label={seg.group}>
                      {seg.options.map(renderNativeOption)}
                    </optgroup>
                  ),
                )
              : options.filter((o) => o.value !== "").map(renderNativeOption)}
          </select>
        ) : null}
      </SelectPrimitive.Root>
    );

    // Sans libellé ni aide : exactement l'arbre historique (aucune enveloppe). Avec : l'enveloppe UIArc.
    if (label == null && description == null) return root;
    return (
      <div className={styles.field}>
        {label != null ? <label htmlFor={controlId}>{label}</label> : null}
        {root}
        {description != null ? <span id={hintId} className={styles.hint}>{description}</span> : null}
      </div>
    );
  },
);
SelectBase.displayName = "Select";

/** Entrée UIArc (`@ubm/ui/components/select`) : LE même composant (aucun défaut divergent). */
export const Select = SelectBase;

export { triggerVariants as selectTriggerVariants };

export default Select;
